import React, { useState, useMemo } from 'react';
import moment from 'moment';

const PAGE_SIZE = 10;

const EVENT_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'academic', label: 'Academic' },
  { value: 'exam', label: 'Exam' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'other', label: 'Other' },
];

const EventList = ({ events, onEdit, onDelete }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');

  // Get unique months from events for filter dropdown
  const months = useMemo(() => {
    const unique = Array.from(
      new Set(events.map(ev => moment(ev.startDate).format('YYYY-MM')))
    );
    return unique.map(m => ({
      value: m,
      label: moment(m, 'YYYY-MM').format('MMMM YYYY'),
    }));
  }, [events]);

  // Filter and search logic
  const filteredEvents = useMemo(() => {
    return events.filter(ev => {
      const matchesType = !typeFilter || ev.eventType === typeFilter;
      const matchesMonth = !monthFilter || moment(ev.startDate).format('YYYY-MM') === monthFilter;
      const matchesSearch =
        !search ||
        ev.eventName.toLowerCase().includes(search.toLowerCase()) ||
        (ev.description && ev.description.toLowerCase().includes(search.toLowerCase()));
      return matchesType && matchesMonth && matchesSearch;
    }).sort((a, b) => new Date(a.startDate) - new Date(b.startDate)); // Sort by date ascending
  }, [events, typeFilter, monthFilter, search]);

  const totalPages = Math.ceil(filteredEvents.length / PAGE_SIZE);
  const paginatedEvents = filteredEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filters/search change
  React.useEffect(() => {
    setPage(1);
  }, [search, typeFilter, monthFilter]);

  return (
    <section className="my-10 max-w-5xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
          <input
            type="text"
            placeholder="🔍 Search events"
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 transition w-full md:w-56"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 transition w-full md:w-40"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            {EVENT_TYPES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 transition w-full md:w-44"
            value={monthFilter}
            onChange={e => setMonthFilter(e.target.value)}
          >
            <option value="">All Months</option>
            {months.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="text-sm text-gray-500 mt-2 md:mt-0">
          Showing <span className="font-semibold">{filteredEvents.length}</span> event{filteredEvents.length !== 1 ? 's' : ''}
        </div>
      </div>
      {paginatedEvents.length === 0 ? (
        <div className="text-gray-400 text-center py-16 text-lg font-medium">
          <span role="img" aria-label="No events">📅</span> No events found.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl">
          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-indigo-50 text-indigo-900">
                <th className="py-3 px-3 text-left rounded-l-xl">Event Name</th>
                <th className="py-3 px-3 text-left">Type</th>
                <th className="py-3 px-3 text-left">Date</th>
                <th className="py-3 px-3 text-left">Description</th>
                <th className="py-3 px-3 text-left rounded-r-xl">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEvents.map(ev => (
                <tr key={ev.id} className="bg-gray-50 hover:bg-indigo-50 transition rounded-xl shadow-sm">
                  <td className="py-2 px-3 font-semibold text-gray-800">{ev.eventName}</td>
                  <td className="py-2 px-3 capitalize">
                    <span className={
                      "inline-block px-2 py-1 rounded text-xs font-semibold " +
                      (ev.eventType === 'holiday'
                        ? 'bg-red-100 text-red-700'
                        : ev.eventType === 'exam'
                        ? 'bg-yellow-100 text-yellow-800'
                        : ev.eventType === 'academic'
                        ? 'bg-blue-100 text-blue-700'
                        : ev.eventType === 'administrative'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700')
                    }>
                      {ev.eventType}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className="inline-block bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-mono">
                      {moment(ev.startDate).format('DD MMM YYYY')}
                      {ev.startDate !== ev.endDate && (
                        <> - {moment(ev.endDate).format('DD MMM YYYY')}</>
                      )}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-600">{ev.description}</td>
                  <td className="py-2 px-3">
                    <button
                      className="inline-flex items-center px-3 py-1 mr-2 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition text-xs font-semibold"
                      onClick={() => onEdit(ev)}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition text-xs font-semibold"
                      onClick={() => onDelete(ev)}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 transition"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          <span className="text-gray-700 font-semibold">
            Page {page} of {totalPages}
          </span>
          <button
            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 transition"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
};

export default EventList;